import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FlaskConical, Eye, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inp, pill, Card } from "./seo/shared";

const NAV = [["Google Analytics", ["ga4"]], ["Google Tag Manager", ["gtm"]], ["Search Console Verification", ["google_verify"]],
  ["Google Ads", ["google_ads"]], ["Meta Pixel", ["meta_pixel"]], ["Meta Domain Verification", ["meta_domain"]],
  ["Bing Verification", ["bing"]], ["Pinterest Verification", ["pinterest"]], ["TikTok Pixel", ["tiktok"]],
  ["Custom Scripts", ["custom_head", "custom_body", "custom_footer"]]];
const STATUS = { Active: "bg-emerald-500/15 text-emerald-400", Inactive: "bg-secondary text-muted-foreground", Error: "bg-destructive/15 text-destructive" };

function CodeForm({ kind, spec, item, onDone, onCancel }) {
  const [value, setValue] = useState(item?.value || "");
  const [label, setLabel] = useState(item?.label || "");
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async () => { try { const { data } = await api.post("/admin/tracking/preview", { kind, value }); setPrev(data); } catch (e) { setPrev(null); toast.error(apiError(e)); } };
  const save = async () => {
    setBusy(true);
    try {
      if (item) await api.put(`/admin/tracking/${item.id}`, { kind, value, label, enabled: item.enabled });
      else await api.post("/admin/tracking", { kind, value, label, enabled: true });
      toast.success("Saved & published to the live site"); onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-primary/40 p-4 space-y-3" data-testid={`track-form-${kind}`}>
      {spec.html ? <textarea rows={5} className={`${inp} font-mono text-xs`} value={value} onChange={(e) => { setValue(e.target.value); setPrev(null); }} placeholder='<script async src="https://…approved domain…"></script>' data-testid="track-value" />
        : <input className={inp} value={value} onChange={(e) => { setValue(e.target.value); setPrev(null); }} placeholder={spec.hint} data-testid="track-value" />}
      <input className={inp} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" data-testid="track-label" />
      {prev && <pre className="text-[11px] bg-secondary/50 rounded-xl p-3 overflow-x-auto whitespace-pre-wrap" data-testid="track-preview-code">{prev.preview}</pre>}
      <div className="flex gap-2">
        <button onClick={preview} disabled={!value} className={`${pill} border border-border`} data-testid="track-preview-btn"><Eye className="h-4 w-4" /> Preview code</button>
        <button onClick={save} disabled={!prev || busy} className={`${pill} bg-primary text-primary-foreground`} data-testid="track-save-btn">{busy ? "Publishing…" : "Save & Publish"}</button>
        <button onClick={onCancel} className={`${pill}`} data-testid="track-cancel-btn"><X className="h-4 w-4" /></button>
      </div>
      {!prev && <p className="text-[11px] text-muted-foreground">Preview the code to enable publishing.</p>}
    </div>
  );
}

function Row({ t, spec, reload }) {
  const [edit, setEdit] = useState(false);
  const [test, setTest] = useState(t.last_test);
  const [busy, setBusy] = useState(false);
  const toggle = async () => { try { await api.put(`/admin/tracking/${t.id}`, { kind: t.kind, value: t.value, label: t.label, enabled: !t.enabled }); reload(); } catch (e) { toast.error(apiError(e)); } };
  const del = async () => { if (!window.confirm("Remove this code from the live site?")) return; await api.delete(`/admin/tracking/${t.id}`); toast.success("Removed"); reload(); };
  const runTest = async () => { setBusy(true); try { const { data } = await api.post(`/admin/tracking/${t.id}/test`); setTest({ ...data, at: new Date().toISOString() }); reload(); } catch (e) { toast.error(apiError(e)); } setBusy(false); };
  if (edit) return <CodeForm kind={t.kind} spec={spec} item={t} onDone={() => { setEdit(false); reload(); }} onCancel={() => setEdit(false)} />;
  return (
    <div className="rounded-xl border border-border p-4" data-testid={`track-item-${t.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS[t.status]}`} data-testid="track-status">{t.status}</span>
        <code className="text-sm break-all flex-1 min-w-0">{spec.html ? `${t.value.slice(0, 80)}${t.value.length > 80 ? "…" : ""}` : t.value}</code>
        {t.label && <span className="text-xs text-muted-foreground">{t.label}</span>}
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={t.enabled} onChange={toggle} data-testid="track-enabled-toggle" /> Enabled</label>
        <button onClick={runTest} disabled={busy} className="p-2 rounded-lg border border-border hover:border-primary" title="Test" data-testid="track-test-btn"><FlaskConical className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} /></button>
        <button onClick={() => setEdit(true)} className="p-2 rounded-lg border border-border hover:border-primary" data-testid="track-edit-btn"><Pencil className="h-4 w-4" /></button>
        <button onClick={del} className="p-2 rounded-lg border border-border text-destructive hover:border-destructive" data-testid="track-delete-btn"><Trash2 className="h-4 w-4" /></button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Last updated {new Date(t.updated_at).toLocaleString()}{t.updated_by ? ` by ${t.updated_by}` : ""}</p>
      {test?.results && <ul className="mt-2 text-xs space-y-0.5" data-testid="track-test-results">{test.results.map((r) => <li key={r} className={r.startsWith("✗") ? "text-destructive" : r.startsWith("✓") ? "text-emerald-400" : "text-muted-foreground"}>{r}</li>)}</ul>}
    </div>
  );
}

export default function TrackingCMS() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState(NAV[0][0]);
  const [adding, setAdding] = useState(null);
  const load = () => api.get("/admin/tracking").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const kinds = NAV.find((n) => n[0] === tab)[1];
  return (
    <div className="grid lg:grid-cols-[230px_1fr] gap-6" data-testid="tracking-cms">
      <nav className="flex lg:flex-col gap-1 overflow-x-auto">
        {NAV.map(([n, ks]) => {
          const c = d.items.filter((i) => ks.includes(i.kind) && i.enabled).length;
          return <button key={n} onClick={() => { setTab(n); setAdding(null); }} data-testid={`track-nav-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`flex items-center justify-between gap-2 text-left whitespace-nowrap px-4 py-2 rounded-xl text-sm transition-colors ${tab === n ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{n}{c > 0 && <span className="text-[10px] px-1.5 rounded-full bg-emerald-500/20">{c}</span>}</button>;
        })}
      </nav>
      <div className="space-y-5 min-w-0">
        {kinds.map((k) => {
          const spec = d.kinds[k];
          const items = d.items.filter((i) => i.kind === k);
          return (
            <Card key={k} title={spec.label} hint={spec.html ? `Only external scripts from approved tracking domains are allowed (${d.allowed_domains.slice(0, 6).join(", ")}…). Inline code is blocked for security.` : `Format: ${spec.hint}. You can paste the full snippet — the ID is extracted automatically.`}
              action={adding !== k && <button onClick={() => setAdding(k)} className={`${pill} border border-border`} data-testid={`track-add-${k}`}><Plus className="h-4 w-4" /> Add</button>}>
              <div className="space-y-3">
                {adding === k && <CodeForm kind={k} spec={spec} onDone={() => { setAdding(null); load(); }} onCancel={() => setAdding(null)} />}
                {items.map((t) => <Row key={t.id} t={t} spec={spec} reload={load} />)}
                {!items.length && adding !== k && <p className="text-sm text-muted-foreground">Not added yet.</p>}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
